/**
 * Heuristica de "qual e o proximo step do wizard" pra um contrato societario.
 *
 * Compartilhado entre:
 * - `criar/index.tsx`: usa pra decidir o step inicial ao retomar via
 *   `?contratoId=`.
 * - `components/ContratoSocietarioCard.tsx`: usa pra mostrar uma badge
 *   "Próximo: <Step>" no card de listagem (sessao 2026-05-08, contrato 73).
 *
 * Logica unica garante que o operador veja a mesma indicacao no card e ao
 * abrir a edicao.
 */
import type { IContratoSocietario } from '../../../../hooks/useSocietario';

export type WizardStepKey =
  | 'tipo_operacao'
  | 'verificacao_empresa'
  | 'contrato_vigente'
  | 'tipos_alteracao'
  | 'empresario_individual'
  | 'socios'
  | 'empresa'
  | 'revisao'
  | 'gerado';

export const WIZARD_STEP_LABELS: Record<WizardStepKey, string> = {
  tipo_operacao: 'Operação',
  verificacao_empresa: 'Verificação',
  contrato_vigente: 'Contrato vigente',
  tipos_alteracao: 'Tipos de alteração',
  empresario_individual: 'Empresário Individual',
  socios: 'Sócios',
  empresa: 'Empresa',
  revisao: 'Revisão',
  gerado: 'Gerado',
};

const TIPOS_AFETAM_SOCIOS = ['quadro_societario', 'dados_socio', 'administracao'];

/**
 * Devolve o proximo step que o operador precisa endereçar pra esse contrato.
 * Retorna `null` quando o contrato esta em estado terminal (gerado/assinado/
 * cancelado/arquivado) — nao ha proxima acao no wizard.
 */
export function computeProximoStep(c: IContratoSocietario): WizardStepKey | null {
  // Estados terminais — sem proxima acao no wizard.
  if (c.status === 'gerado' || c.status === 'assinado') return 'gerado';
  if (c.status === 'cancelado' || c.status === 'arquivado') return null;

  // rascunho_inicial: stub criado pelo upload de contrato vigente.
  if (c.status === 'rascunho_inicial' && c.tipo_operacao === 'alteracao') {
    return 'contrato_vigente';
  }

  // Transformação EI→LTDA: EmpresárioIndividual → Empresa → Sócios → Revisão.
  if (c.tipo_operacao === 'transformacao') {
    const dadosEi = c.dados_ei;
    const temEi = !!dadosEi?.denominacao && !!dadosEi?.nome && !!c.cnpj;
    const temSociosT = (c.socios?.length ?? 0) > 0;
    const temAdminT = (c.socios ?? []).some(
      s => s.tipo_vinculo === 'socio_administrador' || s.tipo_vinculo === 'administrador',
    );
    const temEmpresaT = !!(c.cnpj && c.foro && c.cidade_assinatura);
    if (!temEi) return 'empresario_individual';
    if (!temEmpresaT) return 'empresa';
    if (!temSociosT || !temAdminT) return 'socios';
    return 'revisao';
  }

  const tipos = ((c as any).tipos_alteracao ?? []) as string[];
  const temSocios = (c.socios?.length ?? 0) > 0;
  const temAdminOk = (c.socios ?? []).some(
    s => s.tipo_vinculo === 'socio_administrador' || s.tipo_vinculo === 'administrador',
  );
  const temEmpresaOk = !!(c.cnpj && c.foro && c.cidade_assinatura);
  const tiposAfetamSocios = tipos.some(t => TIPOS_AFETAM_SOCIOS.includes(t));

  if (c.tipo_operacao === 'alteracao' && tipos.length === 0) {
    return 'tipos_alteracao';
  }
  if (c.tipo_operacao === 'constituicao' && (!temSocios || !temAdminOk)) {
    return 'socios';
  }
  if (c.tipo_operacao === 'alteracao' && tiposAfetamSocios && (!temSocios || !temAdminOk)) {
    return 'socios';
  }
  if (!temEmpresaOk) return 'empresa';
  return 'revisao';
}

/**
 * Índice da etapa mais adiantada até onde as bolinhas deixam ir: só o trecho CONTÍNUO de
 * etapas visitadas a partir da primeira (a atual conta como visitada). Marcar um tipo novo
 * em "Tipos de alteração" cria uma etapa no meio (ex.: Sócios) que o operador ainda não viu;
 * o pulo não pode passar por cima dela, nem a bolinha dela aparecer como já alcançada.
 */
export function indiceAlcancado(
  chaves: string[],
  visitadas: ReadonlySet<string>,
  atualIdx: number,
): number {
  let alcancado = atualIdx;
  for (let i = 0; i < chaves.length; i += 1) {
    if (i !== atualIdx && !visitadas.has(chaves[i])) break;
    alcancado = Math.max(alcancado, i);
  }
  return alcancado;
}
